/**
 * Zustand store for the discrete round state.
 *
 * It holds nothing that changes per frame — see roundReducers.ts for why the
 * multiplier is not here. Every action is a thin wrapper over a pure reducer.
 */
import { create } from "zustand";
import type {
  BalanceEvent,
  BetPanel,
  BetsBatchEvent,
  MyBet,
  OnlineEvent,
  RoundBettingEvent,
  RoundCrashEvent,
  RoundPhase,
  RoundStartEvent,
  StateSync,
} from "@crashx/shared";
import {
  applyBalance,
  applyBetsBatch,
  applyMyBet,
  applyOnline,
  applyRoundBetting,
  applyRoundCrash,
  applyRoundStart,
  applyStateSync,
  initialRoundState,
  type RoundStoreState,
} from "./roundReducers.js";

export interface RoundStoreActions {
  onStateSync: (payload: StateSync) => void;
  onRoundBetting: (payload: RoundBettingEvent) => void;
  onRoundStart: (payload: RoundStartEvent) => void;
  onRoundCrash: (payload: RoundCrashEvent) => void;
  onBetsBatch: (payload: BetsBatchEvent) => void;
  onMyBet: (payload: MyBet) => void;
  onBalance: (payload: BalanceEvent) => void;
  onOnline: (payload: OnlineEvent) => void;
}

export type RoundStore = RoundStoreState & RoundStoreActions;

export const useRoundStore = create<RoundStore>()((set) => ({
  ...initialRoundState,
  onStateSync: (payload) => set((s) => applyStateSync(s, payload)),
  onRoundBetting: (payload) => set((s) => applyRoundBetting(s, payload)),
  onRoundStart: (payload) => set((s) => applyRoundStart(s, payload)),
  onRoundCrash: (payload) => set((s) => applyRoundCrash(s, payload)),
  onBetsBatch: (payload) => set((s) => applyBetsBatch(s, payload)),
  onMyBet: (payload) => set((s) => applyMyBet(s, payload)),
  onBalance: (payload) => set((s) => applyBalance(s, payload)),
  onOnline: (payload) => set((s) => applyOnline(s, payload)),
}));

// Selectors return stable references only: never an object literal.

export const selectPhase = (s: RoundStore): RoundPhase => s.round?.phase ?? "IDLE";
export const selectConfig = (s: RoundStore) => s.config;
export const selectRound = (s: RoundStore) => s.round;
export const selectHistory = (s: RoundStore) => s.history;
export const selectBets = (s: RoundStore) => s.bets;
export const selectPreviousBets = (s: RoundStore) => s.previousBets;
export const selectMyBets = (s: RoundStore) => s.myBets;
export const selectOnlinePlayers = (s: RoundStore) => s.onlinePlayers;
export const selectLastCrash = (s: RoundStore) => s.lastCrash;
export const selectReveals = (s: RoundStore) => s.reveals;

/** The live bet on a panel, if any. `cancelled` and settled bets do not count. */
export function selectActiveBet(panel: BetPanel) {
  return (s: RoundStore): MyBet | null =>
    s.myBets.find((b) => b.panel === panel && (b.status === "pending" || b.status === "active")) ?? null;
}

/** Balance for a currency, or null while the server has not sent one. */
export function selectBalance(currency: string) {
  return (s: RoundStore): BalanceEvent | null => s.balances[currency] ?? null;
}
